import { useState } from 'react';
import { FlaskConical, Loader2, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { useConfigStore } from '@/store/useConfigStore';
import { selectOCRInstance, callOCR, containsAnyKeyword } from '@/services/ocrService';
import { Button } from '@/components/ui/Button';
import { Input, Switch } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { TagInput, Tag } from '@/components/ui/Tag';
import type { OCRResult } from '@/types';

export function SizeChartPage() {
    const { sizeChartConfig, updateSizeChartConfig, lowQualityConfig, ocrInstances } = useConfigStore();

    const [testOpen, setTestOpen] = useState(false);

    const hasAvailableInstance = !!selectOCRInstance(ocrInstances);
    const bothEnabled = sizeChartConfig.enabled && lowQualityConfig.enabled;

    return (
        <div className="flex h-full flex-col">
            <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">尺码图识别</h1>
                    <p className="text-xs text-zinc-500">通过 OCR 识别详情图中的尺码图并自动填充</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTestOpen(true)}
                    disabled={!hasAvailableInstance}
                >
                    <FlaskConical size={14} />
                    测试
                </Button>
            </div>

            <div className="flex-1 overflow-auto p-4">
                <div className="mx-auto flex max-w-3xl flex-col gap-4">
                    {/* 说明 */}
                    <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                        <Info size={14} className="mt-0.5 shrink-0" />
                        <span>仅对详情图执行识别，每商品仅记录第一张命中尺码图。</span>
                    </div>

                    {/* OCR 复用提示 */}
                    {bothEnabled && (
                        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                            <Info size={14} className="mt-0.5 shrink-0" />
                            <span>检测到低质量过滤已开启，建议先执行低质量过滤，可复用 OCR 结果减少调用次数。</span>
                        </div>
                    )}

                    {/* 配置面板 */}
                    <section className="rounded-lg border border-zinc-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-sm font-semibold text-zinc-900">配置</h2>
                                <p className="text-xs text-zinc-500">开启后可对详情图执行尺码图识别</p>
                            </div>
                            <Switch
                                checked={sizeChartConfig.enabled}
                                onChange={(v) => updateSizeChartConfig({ enabled: v })}
                                label={sizeChartConfig.enabled ? '已启用' : '已禁用'}
                            />
                        </div>

                        <div className="mt-4">
                            <TagInput
                                label="尺码关键词"
                                tags={sizeChartConfig.keywords}
                                onAdd={(tag) => updateSizeChartConfig({ keywords: [...sizeChartConfig.keywords, tag] })}
                                onRemove={(i) => updateSizeChartConfig({ keywords: sizeChartConfig.keywords.filter((_, idx) => idx !== i) })}
                                placeholder="输入关键词后回车添加"
                            />
                            <p className="mt-1 text-xs text-zinc-400">OCR 识别到的文字包含任一关键词时，该图片将被判定为尺码图</p>
                        </div>
                    </section>
                </div>
            </div>

            <TestModal
                open={testOpen}
                onClose={() => setTestOpen(false)}
                keywords={sizeChartConfig.keywords}
            />
        </div>
    );
}

interface TestModalProps {
    open: boolean;
    onClose: () => void;
    keywords: string[];
}

function TestModal({ open, onClose, keywords }: TestModalProps) {
    const [imageUrl, setImageUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<OCRResult | null>(null);
    const [hits, setHits] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    const handleDetect = async () => {
        if (!imageUrl.trim()) return;
        const instance = selectOCRInstance(useConfigStore.getState().ocrInstances);
        if (!instance) {
            setError('无可用 OCR 实例，请先在 OCR 管理页面添加并启用实例');
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        setHits([]);
        try {
            const res = await callOCR(instance, imageUrl.trim());
            setResult(res);
            if (res.success && res.text) {
                setHits(containsAnyKeyword(res.text, keywords));
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : '识别失败');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setImageUrl('');
        setResult(null);
        setHits([]);
        setError(null);
        setLoading(false);
        onClose();
    };

    const isSizeChart = hits.length > 0;

    return (
        <Modal open={open} onClose={handleClose} title="尺码图检测" width="lg">
            <div className="flex flex-col gap-4">
                <Input
                    label="图片 URL"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="输入要检测的图片链接"
                />
                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleDetect}
                        disabled={loading || !imageUrl.trim()}
                    >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />}
                        识别检测
                    </Button>
                </div>

                {(loading || result || error) && (
                    <div className="flex flex-col gap-3">
                        {error && (
                            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                                {error}
                            </div>
                        )}
                        {result && (
                            <>
                                <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                                    <p className="mb-1.5 text-xs font-medium text-zinc-500">OCR 识别文字</p>
                                    <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
                                        {result.success ? (result.text || '（未识别到文字）') : `识别失败：${result.error}`}
                                    </p>
                                </div>
                                <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                                    <p className="mb-1.5 text-xs font-medium text-zinc-500">判定结果</p>
                                    <div className="flex items-center gap-2">
                                        {isSizeChart ? (
                                            <>
                                                <CheckCircle2 size={16} className="text-green-500" />
                                                <span className="text-sm font-medium text-green-600">判定为尺码图</span>
                                            </>
                                        ) : (
                                            <>
                                                <AlertCircle size={16} className="text-zinc-400" />
                                                <span className="text-sm font-medium text-zinc-500">非尺码图</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                                    <p className="mb-1.5 text-xs font-medium text-zinc-500">命中关键词</p>
                                    {hits.length > 0 ? (
                                        <div className="flex flex-wrap gap-1.5">
                                            {hits.map((kw, i) => (
                                                <Tag key={`${kw}-${i}`} label={kw} color="green" />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-zinc-400">未命中任何关键词</p>
                                    )}
                                </div>
                            </>
                        )}
                        {loading && !result && !error && (
                            <div className="flex items-center gap-2 text-sm text-zinc-400">
                                <Loader2 size={14} className="animate-spin" />
                                正在识别中...
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
}
